import type { SupWeb3VaultBalance } from "@/types";
import React, { useMemo, useState } from "react";

import LoaderInPage from "@/components/Loader/InPage";
import PortfolioSummaryWidget from "@/components/Portfolio/Superpositions/Widget";

import { SupOption, SupSelect, SupText } from "@repo/core-ui";

import usePortfolioWidgets, { type PortfolioSummarySingleWidget, type PortfolioWidgetsGroupBy } from "@/hooks/data/usePortfolioWidgets";
import { useStoredData } from "@/store";

const groupByOptions = {
  protocols: "by protocol",
  vault_chains: "by vault chain",
  position_chains: "by position chain",
} as Record<PortfolioWidgetsGroupBy, string>;

interface PortfolioSuperpositionsTokenListProps {
  superPositions: SupWeb3VaultBalance[];
  portfolioValue: number;
  isLoading: boolean;
}

export default function PortfolioSuperpositionsTokenList({ superPositions: rawSuperPositions, portfolioValue, isLoading }: PortfolioSuperpositionsTokenListProps) {
  const storedData = useStoredData();

  const superPositions = useMemo(
    () => (storedData.showDust ? rawSuperPositions : rawSuperPositions.filter(balance => balance.superposition_usd_value > 0.01)),
    [rawSuperPositions, storedData.showDust]
  );

  const [groupBy, setGroupBy] = useState<PortfolioWidgetsGroupBy>("protocols");
  const [showAll, setShowAll] = useState(false);
  const widgets = usePortfolioWidgets(superPositions, groupBy);

  const displayedWidgets = useMemo(() => {
    if (showAll || widgets.length <= 5) {
      return widgets;
    }
    return widgets.slice(0, 5);
  }, [widgets, showAll]);

  return isLoading ? (
    <div className="flex h-[134px] w-full items-center justify-center">
      <LoaderInPage />
    </div>
  ) : (
    <div className=" pb-4">
      <header className="relative mb-4 flex flex-row items-center justify-between text-neutral-900">
        <SupSelect
          renderSelectedOption={option => <SupText variant="caption">{groupByOptions[option.value]}</SupText>}
          value={groupBy}
          className="relative h-5 w-fit whitespace-nowrap border-none bg-transparent pl-0 hover:bg-transparent focus:bg-transparent active:bg-transparent"
          popoverClassName="p-0 min-w-36"
          popoverContentClassName="p-0"
          onSelect={setGroupBy}
          renderOptionLabel={option => (
            <SupText variant="caption" className="w-full">
              {option.label}
            </SupText>
          )}
          optionClassName="h-6 p-2"
        >
          {Object.entries(groupByOptions).map(item => (
            <SupOption key={`summary-group-by-${item[0]}`} label={item[1]} value={item[0]} />
          ))}
        </SupSelect>
      </header>

      <div className="flex flex-auto flex-wrap gap-2 align-middle">
        {displayedWidgets.map((singleWidget: PortfolioSummarySingleWidget, index) => (
          <PortfolioSummaryWidget
            badge={singleWidget.badge}
            groupBy={groupBy}
            name={singleWidget.name}
            value={singleWidget.usdValue}
            totalPortfolioValue={portfolioValue}
            key={`portfolio-summary-widget-${groupBy}-${index}`}
          />
        ))}
      </div>
      {!showAll && widgets.length > 5 && (
        <button onClick={() => setShowAll(true)}>
          <SupText variant="body2" family="mono" className="my-4 text-neutral-950">
            +{widgets.length - 5} MORE ...
          </SupText>
        </button>
      )}
    </div>
  );
}
